/* eslint-disable no-use-before-define */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/require-default-props */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TimePeriod, TimePeriodSelect, TimePeriodSelectClasses, TimePeriodSelectProps
} from '@workboard/ui-okr'
import moment from 'moment'

type DurationSelectProps = {
  id?: string;
  isKr?: boolean;
  config?: TimePeriodSelectProps['config'];
  initialValue?: TimePeriodOption;
  onChange?: (value: TimePeriodOption) => void;
  timePeriodOptions?: TimePeriodOption[];
  disabled?: boolean;
  minAllowedDate?: string;
  maxAllowedDate?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Rendered after the required asterisk on the Duration label (e.g. help icon). */
  labelAddon?: React.ReactNode;
};

export const DurationSelect = (props: DurationSelectProps) => {
  const {
    minAllowedDate, maxAllowedDate, initialValue, onChange, disabled, className = '', style,
    isKr = false, config, id, labelAddon
  } = props;
  const { t } = useTranslation();
  const defaultId = React.useId();
  const inputId = id || defaultId;

  return (
    <div className={`DurationSelect ${className}`} style={style}>
      {styleOverrides}
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <div
        className="DurationSelect-labelRow"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        <label className="inLineBlk aEStartDate" htmlFor={inputId} style={{ marginRight: 8 }}>
          {t('Duration')} <span className="text-style-1">*</span>
        </label>
        {labelAddon}
      </div>
      <TimePeriodSelect
        key={initialValue?.name}
        id={inputId}
        disabled={disabled}
        sx={{
          width: 'auto',
        }}
        config={{
          useOwnLocalizationProvider: false,
          enableAutoDefineUnknownTimePeriod: true,
          minAllowedDate,
          maxAllowedDate,
          multiTimePeriods: false,
          validateCustomDateRange: (
            startDate: moment.Moment,
            endDate: moment.Moment,
            errors: { startErrorMessage?: string; endErrorMessage?: string }
          ) => {
            if (
              minAllowedDate &&
              maxAllowedDate &&
              startDate?.isValid() &&
              endDate?.isValid() &&
              startDate?.isBefore(endDate) &&
              (moment.utc(startDate.format('YYYY-MM-DD'))?.isBefore(minAllowedDate) ||
                moment.utc(endDate.format('YYYY-MM-DD'))?.isAfter(maxAllowedDate))
            ) {
              return {
                startErrorMessage: true,
                endErrorMessage: isKr ? 'Duration must fit within parent objective duration' : 'Duration must fit within allowed range',
              };
            }

            return errors;
          },
          ...config,
        }}
        value={initialValue ? [initialValue] : undefined}
        onChange={(selectedTimePeriods: TimePeriod[]) => {
          onChange?.(getTimePeriodOptionFromTimePeriod(selectedTimePeriods[0]));
        }}
        IconComponent={() => <span
          className="arrowDown"
          style={{
            position: 'absolute',
            right: '9px',
            top: 'calc(50% - 0.3em)',
            pointerEvents: 'none',
            color: 'rgba(0, 0, 0, 0.54)',
          }}
        />}
        renderValue={(selectedTimePeriods: TimePeriod[]) => {
          const selected = selectedTimePeriods[0];
          const testId = 'duration-select-value';
          if (!selected) return <span className="duration-select-placeholder">{t('Select Time Period')}</span>;
          if ('fiscalYear' in selected && selected.fiscalYear && selected.label) {
            return <span className="duration-select-value" data-testid={testId}>{selected.label}</span>;
          }
          return (
            <span className="duration-select-value" data-testid={testId}>{customPeriodName(selected.startDate, selected.endDate)}</span>
          );
        }}
      />
    </div>
  );
};

const styleOverrides = (
  <style>
    {`
    .${TimePeriodSelectClasses.formControl},
    .${TimePeriodSelectClasses.select} {
      height: 38px;
    }

    .${TimePeriodSelectClasses.select} {
      background-color: #f2f2f2;

      .MuiSelect-select.MuiInputBase-input {
        padding: 0 32px 0 12px;
        padding-right: 45px;
      }

      svg.MuiSelect-icon {
        color: #0000008a;
      }

      .duration-select-value,
      .duration-select-placeholder {
        font-size: 14px;
        font-family: Lato, "Helvetica Neue", Arial, Helvetica, sans-serif;
      }
      .duration-select-placeholder {
        font-style: italic;
      }
    }

    .${TimePeriodSelectClasses.popover},
    .${TimePeriodSelectClasses.customDatePicker} {
      z-index: 9999999 !important;
    }

    .${TimePeriodSelectClasses.popover} {
      .MuiLinearProgress-root  {
        background-color: rgb(158, 201, 223);

        .MuiLinearProgress-bar {
          background-color: rgb(0, 114, 171);
        }
      }
    }

    .${TimePeriodSelectClasses.customDatePicker} {
      .MuiButtonBase-root.Mui-selected {
        background: #0072AB;
      }
    }

    .${TimePeriodSelectClasses.popover} {
      .${TimePeriodSelectClasses.customDateInput}
        .MuiInputBase-root {
          height: 48px;
          border-width: 1px;
          border-style: solid;
          border-color: rgb(132, 144, 163);
          font-family: Lato;

          svg.MuiSvgIcon-root {
            color: #0000008a;
          }

          .MuiOutlinedInput-notchedOutline {
            border: none;
          }
        }
      }

      .MuiButton-root {
        text-transform: capitalize;
        height: 32px;
        font-family: Lato;

        &.MuiButton-textPrimary:first-child  {
          color: #0072ab;
        }

        /* for some reason the Apply button doesnt have the classes we need to target it */
        &.MuiButton-textPrimary:nth-child(2) {
          background-color: #0072ab;
          color: #ffffff;
        }
      }

    }
  `}
  </style>
);

type TimePeriodOption = {
  start: string;
  end: string;
  name?: string;
  year?: string | number;
  period?: number;
  value?: number;
  quarter_name?: string;
  isQuarter?: boolean;
  yearOffset?: number;
  format?: string;
  _isCustom?: boolean;
  _isFullYear?: boolean;
  _isAllTime?: boolean;
  _isCurrent?: boolean;
  stats?: number;
};

function customPeriodName(start: string, end: string) {
  return `${moment.utc(start).format('MMM DD, YYYY')} - ${moment.utc(end).format('MMM DD, YYYY')}`;
}

function getTimePeriodOptionFromTimePeriod (
  timePeriod: TimePeriod | undefined
): TimePeriodOption {
  const periodName = timePeriod?.label?.match(/([Q|T|H]\d+)/i)?.[0];

  return {
    name: timePeriod?.label,
    year: timePeriod?.fiscalYear,
    _isCustom: !timePeriod?.fiscalYear,
    quarter_name: periodName,
    isQuarter: !!periodName,
    _isFullYear: !periodName && timePeriod?.fiscalYear,
    start: timePeriod?.startDate,
    end: timePeriod?.endDate,
  };
}
